import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import FormBuilder from './FormBuilder';
import MarkdownArticle from './MarkdownArticle';
import ProfileCardAsset from './ProfileCardAsset';
import RichContentEditor from './RichContentEditor';
import ShareFormRenderer from './ShareFormRenderer';
import {
  buildYouTubeEmbedUrl,
  getYouTubeVideoId,
  parseShareListItems,
  shareListItemsToText,
} from '../services/assetsService';
import { storageService } from '../services/storageService';
import { calculateDiscountedPrice, formatPrice, getEffectivePrice, hasDiscountedPrice, parsePrice } from '../utils/currency';
import { optimiseImageFile } from '../utils/imageOptimisation';
import { listingSpecsForAsset, listingTypeConfigFor } from '../utils/listingTypes';
import { buildAssetShareUrl } from '../utils/shareLinks';

const currencyOptions = ['ZAR', 'USD', 'EUR', 'GBP'];
let pdfJsLibPromise = null;
const loadPdfJs = async () => {
  if (!pdfJsLibPromise) {
    pdfJsLibPromise = import('pdfjs-dist/legacy/build/pdf.mjs').then((pdfjsLib) => {
      pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;
      return pdfjsLib;
    });
  }
  return pdfJsLibPromise;
};
const isValidHexColor = (value = '') => /^#(?:[0-9a-f]{3}){1,2}$/i.test(value.trim());
const defaultAssetFields = {
  title: true,
  description: true,
  price: true,
  quantity: true,
  area: true,
  sizes: false,
  colors: false,
  link: false,
  multipleImages: false,
  details: true,
  enquire: false,
  share: true,
  updates: false,
};

const assetTypeForFile = (file) => {
  if (!file) return 'Asset';
  if (file.type.includes('pdf')) return 'Document';
  if (file.type.includes('image')) return 'Image';
  if (file.type.includes('video')) return 'Video';
  return 'File';
};

const assetShapeForFile = (file) => (
  file?.type.includes('image') || file?.type.includes('video') ? 'standard' : 'document'
);

const previewForFile = (file) => {
  if (!file) return null;
  if (file.type.includes('image') || file.type.includes('video') || file.type.includes('pdf')) {
    return URL.createObjectURL(file);
  }
  return null;
};

const formatFileSize = (bytes = 0) => `${Math.max(bytes / 1000000, 0.1).toFixed(1)} MB`;

const imageUrlForItemImage = (image = {}) => image.url || image.downloadUrl || image.preview || '';

const ShareIcon = () => (
  <svg
    aria-hidden="true"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <path d="m8.59 13.51 6.83 3.98" />
    <path d="m15.41 6.51-6.82 3.98" />
  </svg>
);

const LinkIcon = () => (
  <svg
    aria-hidden="true"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const EyeIcon = () => (
  <svg
    aria-hidden="true"
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="m15 18-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    aria-hidden="true"
    className="h-5 w-5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);


const normaliseAssetLink = (value = '') => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return '';
  if (/^https?:\/\//i.test(trimmedValue)) return trimmedValue;
  return `https://${trimmedValue}`;
};

const isVideoAsset = (asset) => {
  const contentType = asset.contentType || '';
  const url = asset.downloadUrl || asset.preview || '';
  return asset.type === 'Video' ||
    contentType.startsWith('video/') ||
    /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(url);
};

const isPdfAsset = (asset) => {
  const contentType = asset.contentType || '';
  const url = asset.downloadUrl || asset.preview || '';
  return asset.type === 'Document' ||
    contentType === 'application/pdf' ||
    /\.pdf(\?|#|$)/i.test(url);
};

const hasRenderablePdfSource = (asset, url = '') => (
  Boolean(url && !url.startsWith('#')) &&
  (
    asset.contentType === 'application/pdf' ||
    /\.pdf(\?|#|$)/i.test(url)
  )
);

const isImageAsset = (asset) => {
  const contentType = asset.contentType || '';
  const url = asset.downloadUrl || asset.preview || '';
  return asset.type === 'Image' ||
    contentType.startsWith('image/') ||
    /\.(avif|gif|jpe?g|png|svg|webp)(\?|#|$)/i.test(url);
};

const isHtmlEmbedAsset = (asset) => asset.type === 'HTML Embed' || Boolean(asset.embedHtml);
const isYouTubeVideoAsset = (asset) => (
  asset.type === 'YouTube Video' ||
  Boolean(asset.youtubeVideoId || asset.youtubeEmbedUrl)
);
const isProfileCardAsset = (asset) => (
  asset.type === 'Profile Card' ||
  asset.type === 'Link In Bio URL' ||
  Boolean(asset.externalUrl)
);
const isLinkInBioAsset = (asset) => (
  asset.type === 'Link In Bio' ||
  Array.isArray(asset.linkItems)
);
const isMarkdownArticleAsset = (asset) => (
  asset.type === 'Rich Content' ||
  asset.type === 'Markdown Article' ||
  Boolean(asset.articleMarkdown)
);
const isFormAsset = (asset) => asset.type === 'Form' || Boolean(asset.form?.fields);
const isShareListAsset = (asset) => asset.type === 'List' || Array.isArray(asset.listItems);

const displayAssetType = (asset) => (
  isProfileCardAsset(asset) ? 'Profile Card' : isFormAsset(asset) ? 'Form' : isShareListAsset(asset) ? 'List' : asset.type || 'Asset'
);

const buildPdfPreviewUrl = (url = '', options = {}) => {
  const baseUrl = url.split('#')[0];
  if (!baseUrl) return '';

  if (options.presentation) {
    const page = Math.max(Math.round(Number(options.page) || 1), 1);
    return `${baseUrl}#page=${page}&toolbar=0&navpanes=0&scrollbar=0&view=Fit`;
  }

  return `${baseUrl}#toolbar=0&navpanes=0&zoom=page-width`;
};

const linkItemsToText = (linkItems = []) => linkItems
  .map((item) => [item.label, item.url].filter(Boolean).join(' | '))
  .join('\n');

const parseLinkItems = (value) => value
  .split('\n')
  .map((line) => line.trim())
  .filter(Boolean)
  .map((line) => {
    const [label, url] = line.includes('|')
      ? line.split('|').map((part) => part.trim())
      : [line, line];

    return {
      label: label || url,
      url,
    };
  })
  .filter((item) => item.url);

const normaliseOptionList = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value || '')
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean);
};

function OptionTagEditor({
  inputId,
  label,
  onChange,
  placeholder,
  value,
}) {
  const [draft, setDraft] = useState('');
  const options = normaliseOptionList(value);

  const addOption = () => {
    const nextOption = draft.trim();
    if (!nextOption) return;

    onChange([...options, nextOption]);
    setDraft('');
  };

  const removeOption = (optionToRemove) => {
    onChange(options.filter((option) => option !== optionToRemove));
  };

  return (
    <div>
      <label className="field-label" htmlFor={inputId}>{label}</label>
      <div className="flex gap-2">
        <input
          className="field"
          id={inputId}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addOption();
            }
          }}
          placeholder={placeholder}
          value={draft}
        />
        <button
          className="shrink-0 rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!draft.trim()}
          onClick={addOption}
          type="button"
        >
          Add
        </button>
      </div>
      {options.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {options.map((option) => (
            <span className="chip inline-flex items-center gap-2" key={option}>
              {option}
              <button
                aria-label={`Remove ${option}`}
                className="text-slate-500 transition hover:text-slate-950"
                onClick={() => removeOption(option)}
                type="button"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const normaliseComboProductIds = (value) => (
  Array.isArray(value)
    ? value
      .map((item) => (typeof item === 'object' ? item.id : item))
      .map((item) => String(item || '').trim())
      .filter(Boolean)
    : []
);

const formatComboAmount = (amount, hasProducts) => (
  hasProducts ? amount.toFixed(2) : ''
);

const formatCalculatedAmount = (amount, hasValue) => (
  hasValue ? amount.toFixed(2) : ''
);

const effectiveStorePriceForAsset = (item = {}) => (
  String(item.storeBasePrice || '').trim()
    ? parsePrice(item.price)
    : getEffectivePrice(item)
);

const profitForAsset = (item = {}) => {
  const hasValue = String(item.price || item.unitCost || item.discountValue || '').trim() !== '';
  const effectivePrice = effectiveStorePriceForAsset(item);
  return formatCalculatedAmount(effectivePrice - parsePrice(item.unitCost), hasValue);
};

const basePriceForStoreAsset = (item = {}) => (
  String(item.storeBasePrice || '').trim() || String(item.price || '')
);

const comboAmountsForProducts = (
  productIds,
  productOptions,
  discountType = 'percentage',
  discountValue = '',
) => {
  const selectedProducts = productIds
    .map((productId) => productOptions.find((product) => product.id === productId))
    .filter(Boolean);
  const hasProducts = selectedProducts.length > 0;
  const basePrice = selectedProducts.reduce((sum, product) => sum + effectiveStorePriceForAsset(product), 0);
  const unitCost = selectedProducts.reduce((sum, product) => sum + parsePrice(product.unitCost), 0);
  const discountedPrice = discountValue === ''
    ? basePrice
    : calculateDiscountedPrice(basePrice, discountType, discountValue);

  return {
    hasProducts,
    basePrice,
    price: discountedPrice,
    unitCost,
    profit: discountedPrice - unitCost,
  };
};

function ComboProductEditor({
  assetId,
  onChange,
  productOptions = [],
  value,
}) {
  const [selectedProductId, setSelectedProductId] = useState('');
  const selectedProductIds = normaliseComboProductIds(value);
  const selectedProductIdSet = new Set(selectedProductIds);
  const selectedProducts = selectedProductIds
    .map((productId) => productOptions.find((product) => product.id === productId))
    .filter(Boolean);
  const filteredProducts = productOptions
    .filter((product) => !selectedProductIdSet.has(product.id));
  const selectableProductId = filteredProducts.some((product) => product.id === selectedProductId)
    ? selectedProductId
    : filteredProducts[0]?.id || '';

  const addProduct = () => {
    if (!selectableProductId) return;
    onChange([...selectedProductIds, selectableProductId]);
    setSelectedProductId('');
  };

  const removeProduct = (productIdToRemove) => {
    onChange(selectedProductIds.filter((productId) => productId !== productIdToRemove));
  };

  return (
    <div>
      <label className="field-label" htmlFor={`asset-combo-product-${assetId}`}>Combo products</label>
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <select
          className="field min-w-0"
          disabled={!filteredProducts.length}
          id={`asset-combo-product-${assetId}`}
          onChange={(event) => setSelectedProductId(event.target.value)}
          value={selectableProductId}
        >
          {filteredProducts.length ? (
            filteredProducts.map((product) => (
              <option key={product.id} value={product.id}>
                {product.title || product.name || 'Untitled product'}
              </option>
            ))
          ) : (
            <option value="">No matching products</option>
          )}
        </select>
        <button
          aria-label="Add combo product"
          className="grid h-10 w-10 shrink-0 place-items-center rounded border border-slate-300 bg-white text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!selectableProductId}
          onClick={addProduct}
          title="Add combo product"
          type="button"
        >
          <svg
            aria-hidden="true"
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
        </button>
      </div>
      {selectedProducts.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {selectedProducts.map((product) => (
            <span className="chip inline-flex items-center gap-2" key={product.id}>
              {product.title || product.name || 'Untitled product'}
              <button
                aria-label={`Remove ${product.title || product.name || 'product'}`}
                className="text-slate-500 transition hover:text-slate-950"
                onClick={() => removeProduct(product.id)}
                type="button"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
      )}
      {!productOptions.length && (
        <p className="mt-2 text-xs font-semibold text-slate-500">
          Add another product to this grid before creating a combo.
        </p>
      )}
    </div>
  );
}

function ListingFieldsEditor({ asset, listingType, onChange }) {
  const config = listingTypeConfigFor(asset.listingType || listingType);
  const values = asset.listingFields || {};

  const updateListingField = (fieldId, value) => {
    onChange({
      ...asset,
      listingType: config.id,
      listingFields: {
        ...values,
        [fieldId]: value,
      },
    });
  };

  return (
    <div className="mt-4 border-t border-slate-200 pt-4">
      <p className="text-sm font-semibold text-slate-950">{config.label} fields</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {config.fields.map((field) => (
          <div key={field.id}>
            <label className="field-label mt-0" htmlFor={`asset-listing-${asset.id}-${field.id}`}>
              {field.label}
            </label>
            {field.type === 'select' ? (
              <select
                className="field"
                id={`asset-listing-${asset.id}-${field.id}`}
                value={values[field.id] || ''}
                onChange={(event) => updateListingField(field.id, event.target.value)}
              >
                <option value="">Select {field.label.toLowerCase()}</option>
                {(field.options || []).map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            ) : (
              <input
                className="field"
                id={`asset-listing-${asset.id}-${field.id}`}
                inputMode={field.type === 'number' ? 'decimal' : undefined}
                placeholder={field.suffix ? field.suffix : field.label}
                type="text"
                value={values[field.id] || ''}
                onChange={(event) => updateListingField(field.id, event.target.value)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ListingSpecs({ specs, className = '' }) {
  if (!specs.length) return null;

  const renderValue = (spec) => {
    const value = spec.shortLabel ? `${spec.value} ${spec.shortLabel}` : spec.value;
    if (spec.suffix !== 'm2') return value;

    const valueWithoutSuffix = String(spec.value).replace(/\s*m2$/i, '');
    return (
      <>
        {valueWithoutSuffix} m<sup>2</sup>
      </>
    );
  };

  const renderIcon = (icon) => {
    if (!icon) return null;
    const iconClassName = "h-4 w-4 shrink-0";
    const commonProps = {
      'aria-hidden': 'true',
      className: iconClassName,
      fill: 'none',
      stroke: 'currentColor',
      strokeLinecap: 'round',
      strokeLinejoin: 'round',
      strokeWidth: '2',
      viewBox: '0 0 24 24',
    };

    if (icon === 'bed') {
      return (
        <svg {...commonProps}>
          <path d="M3 7v10" />
          <path d="M21 12v5" />
          <path d="M3 12h18" />
          <path d="M7 12V9h5a3 3 0 0 1 3 3" />
          <path d="M3 17h18" />
        </svg>
      );
    }

    if (icon === 'bath') {
      return (
        <svg {...commonProps}>
          <path d="M4 12h16" />
          <path d="M6 12V6a2 2 0 0 1 4 0" />
          <path d="M5 12v3a5 5 0 0 0 10 0v-3" />
          <path d="M8 19v2" />
          <path d="M16 19v2" />
        </svg>
      );
    }

    if (icon === 'home') {
      return (
        <svg {...commonProps}>
          <path d="m3 11 9-8 9 8" />
          <path d="M5 10v10h14V10" />
          <path d="M10 20v-6h4v6" />
        </svg>
      );
    }

    if (icon === 'land') {
      return (
        <svg {...commonProps}>
          <path d="M4 18 9 6l6 12" />
          <path d="m12 12 3-6 5 12" />
          <path d="M3 18h18" />
        </svg>
      );
    }

    if (icon === 'car') {
      return (
        <svg {...commonProps}>
          <path d="M5 17h14" />
          <path d="M6 17v-5l2-5h8l2 5v5" />
          <path d="M8 17v2" />
          <path d="M16 17v2" />
          <path d="M7 12h10" />
          <circle cx="8" cy="15" r="1" />
          <circle cx="16" cy="15" r="1" />
        </svg>
      );
    }

    if (icon === 'users') {
      return (
        <svg {...commonProps}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    }

    return null;
  };

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {specs.slice(0, 6).map((spec) => (
        <span className="chip asset-meta-chip inline-flex min-w-14 flex-col items-center gap-1 text-center" key={spec.id}>
          {renderIcon(spec.icon)}
          <span>{renderValue(spec)}</span>
        </span>
      ))}
    </div>
  );
}

function ShareListAsset({ asset }) {
  const items = Array.isArray(asset.listItems) ? asset.listItems : [];
  const tags = normaliseOptionList(asset.tags);
  const actionUrl = normaliseAssetLink(asset.actionUrl || asset.linkUrl || '');
  const hasAction = Boolean(actionUrl && asset.actionLabel);

  return (
    <div className="asset-share-list grid gap-4 p-4 text-left normal-case tracking-normal text-slate-950">
      <div className="flex min-w-0 items-start gap-3">
        {asset.iconUrl && (
          <img
            alt={asset.title || 'List'}
            className="asset-share-list-cover h-14 w-14 shrink-0 rounded border border-slate-200 object-cover"
            src={asset.iconUrl}
          />
        )}
        <div className="min-w-0">
          <p className="label-caps">{items.length} item{items.length === 1 ? '' : 's'}</p>
          <h3 className="mt-1 text-lg font-semibold leading-snug text-slate-950">{asset.title || 'Untitled list'}</h3>
          {asset.description && (
            <div className="asset-description mt-2 text-sm text-slate-600">
              <ReactMarkdown>{asset.description}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag) => <span className="chip" key={tag}>{tag}</span>)}
        </div>
      )}
      <div className="grid gap-2">
        {items.slice(0, 8).map((item, index) => {
          const itemTags = normaliseOptionList(item.tags);
          const itemActionUrl = normaliseAssetLink(item.actionUrl || item.url || '');

          return (
            <div className="rounded border border-slate-200 bg-white px-3 py-3" key={item.id || `${item.title}-${index}`}>
              <div className="flex min-w-0 items-start gap-3">
                {item.imageUrl && (
                  <img
                    alt={item.title || 'List item'}
                    className="asset-share-list-image h-10 w-10 shrink-0 rounded border border-slate-200 object-cover"
                    src={item.imageUrl}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
                    <p className="min-w-0 text-sm font-semibold leading-snug text-slate-950">{item.title || `Item ${index + 1}`}</p>
                    {itemActionUrl && item.actionLabel && (
                      <a
                        className="shrink-0 rounded border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                        href={itemActionUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {item.actionLabel}
                      </a>
                    )}
                  </div>
                  {item.detail && <p className="mt-1 text-xs leading-5 text-slate-600">{item.detail}</p>}
                  {itemTags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {itemTags.map((tag) => <span className="chip text-[0.65rem]" key={tag}>{tag}</span>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {items.length > 8 && (
        <p className="text-xs font-semibold text-slate-500">+{items.length - 8} more item{items.length - 8 === 1 ? '' : 's'}</p>
      )}
      {hasAction && (
        <a
          className="rounded bg-slate-950 px-3 py-2 text-center text-sm font-semibold text-white transition hover:bg-slate-800"
          href={actionUrl}
          rel="noreferrer"
          target="_blank"
        >
          {asset.actionLabel}
        </a>
      )}
    </div>
  );
}

const DEFAULT_HTML_EMBED_HEIGHT = 160;
const VIEWPORT_HTML_EMBED_HEIGHT = 'min(768px, 100svh)';

const usesViewportHeightSizing = (html = '') => (
  /\b\d+(?:\.\d+)?(?:vh|svh|lvh|dvh|vmin|vmax)\b/i.test(html)
);

const buildEmbedResizeScript = (frameId) => `
    <script>
      (function () {
        var frameId = ${JSON.stringify(frameId)};
        var lastHeight = 0;
        var ticks = 0;
        var resize = function () {
          var body = document.body;
          var root = document.documentElement;
          var height = Math.max(
            body ? body.scrollHeight : 0,
            body ? body.offsetHeight : 0,
            root ? root.scrollHeight : 0,
            root ? root.offsetHeight : 0
          );

          if (Math.abs(height - lastHeight) < 2) return;
          lastHeight = height;

          window.parent.postMessage({
            type: 'sharabl-html-embed-height',
            frameId: frameId,
            height: height
          }, '*');
        };

        window.addEventListener('load', resize);
        window.addEventListener('resize', resize);
        if (window.ResizeObserver) {
          new ResizeObserver(resize).observe(document.body);
        }

        resize();
        var interval = window.setInterval(function () {
          ticks += 1;
          resize();
          if (ticks > 20) window.clearInterval(interval);
        }, 250);
      })();
    </script>`;

const buildEmbedSrcDoc = (embedHtml, frameId, autoResize = true) => `
<!doctype html>
<html>
  <head>
    <base target="_blank" />
    <style>
      html,
      body {
        margin: 0;
        overflow: hidden;
      }

      body > *:first-child {
        margin-top: 0;
      }

      body > *:last-child {
        margin-bottom: 0;
      }

      iframe,
      img,
      video {
        max-width: 100%;
      }
    </style>
  </head>
  <body>
    ${embedHtml || ''}
    ${autoResize ? buildEmbedResizeScript(frameId) : ''}
  </body>
</html>`;

function HtmlEmbedFrame({ enableFullscreen = false, html, title }) {
  const frameShellRef = useRef(null);
  const isViewportSized = useMemo(() => usesViewportHeightSizing(html), [html]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [height, setHeight] = useState(DEFAULT_HTML_EMBED_HEIGHT);
  const frameId = useMemo(() => `html-embed-${Math.random().toString(36).slice(2)}`, []);
  const srcDoc = useMemo(
    () => buildEmbedSrcDoc(html, frameId, !isViewportSized),
    [frameId, html, isViewportSized]
  );

  useEffect(() => {
    setHeight(DEFAULT_HTML_EMBED_HEIGHT);
  }, [html]);

  useEffect(() => {
    if (isViewportSized) return undefined;

    const handleMessage = (event) => {
      if (
        event.data?.type !== 'sharabl-html-embed-height' ||
        event.data?.frameId !== frameId
      ) {
        return;
      }

      const nextHeight = Math.ceil(Number(event.data.height) || 0);
      if (nextHeight > 0) {
        setHeight((currentHeight) => {
          const normalisedHeight = Math.max(nextHeight, 120);
          return Math.abs(normalisedHeight - currentHeight) < 2 ? currentHeight : normalisedHeight;
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [frameId, isViewportSized]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === frameShellRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    const shell = frameShellRef.current;
    if (!shell) return;

    try {
      if (document.fullscreenElement === shell) {
        await document.exitFullscreen();
      } else if (shell.requestFullscreen) {
        await shell.requestFullscreen();
      }
    } catch (error) {
      // Fullscreen may be denied by browser policy; leave the embed unchanged.
    }
  };

  return (
    <div
      className={`asset-embed-shell ${isFullscreen ? 'is-fullscreen' : ''}`}
      ref={frameShellRef}
    >
      <iframe
        allowFullScreen={enableFullscreen}
        className="asset-embed-frame"
        sandbox="allow-forms allow-popups allow-presentation allow-scripts"
        scrolling={isFullscreen ? 'auto' : 'no'}
        srcDoc={srcDoc}
        style={{ height: isFullscreen ? '100%' : isViewportSized ? VIEWPORT_HTML_EMBED_HEIGHT : `${height}px` }}
        title={title}
      />
      {enableFullscreen && (
        <button
          aria-label={isFullscreen ? 'Exit fullscreen embed' : 'View embed fullscreen'}
          className="asset-embed-fullscreen-button"
          onClick={toggleFullscreen}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          type="button"
        >
          {isFullscreen ? (
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="M9 3v6H3" />
              <path d="m3 9 6-6" />
              <path d="M15 3v6h6" />
              <path d="m21 9-6-6" />
              <path d="M9 21v-6H3" />
              <path d="m3 15 6 6" />
              <path d="M15 21v-6h6" />
              <path d="m21 15-6 6" />
            </svg>
          ) : (
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="M3 9V3h6" />
              <path d="m3 3 7 7" />
              <path d="M21 9V3h-6" />
              <path d="m21 3-7 7" />
              <path d="M3 15v6h6" />
              <path d="m3 21 7-7" />
              <path d="M21 15v6h-6" />
              <path d="m21 21-7-7" />
            </svg>
          )}
        </button>
      )}
    </div>
  );
}

function PdfPresentationViewer({ asset, assetUrl }) {
  const viewerRef = useRef(null);
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [activePage, setActivePage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [pdfDocument, setPdfDocument] = useState(null);
  const [loadStatus, setLoadStatus] = useState('loading');
  const [hasRenderedSlide, setHasRenderedSlide] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const title = asset.title || 'PDF';

  useEffect(() => {
    setActivePage(1);
    setHasRenderedSlide(false);
  }, [assetUrl]);

  useEffect(() => {
    let isMounted = true;
    let loadingTask = null;

    setPdfDocument(null);
    setPageCount(0);
    setLoadStatus('loading');
    setHasRenderedSlide(false);

    loadPdfJs()
      .then((document) => {
        if (!isMounted) return null;
        loadingTask = document.getDocument({
          cMapPacked: true,
          url: assetUrl,
        });
        return loadingTask.promise;
      })
      .then((document) => {
        if (!document) return;
        if (isMounted) {
          setPdfDocument(document);
          setPageCount(document.numPages || 0);
          setLoadStatus('ready');
        } else {
          document.destroy();
        }
      })
      .catch(() => {
        if (isMounted) setLoadStatus('error');
      });

    return () => {
      isMounted = false;
      loadingTask?.destroy();
    };
  }, [assetUrl]);

  useEffect(() => {
    if (!pageCount) return;
    setActivePage((currentPage) => Math.min(Math.max(currentPage, 1), pageCount));
  }, [pageCount]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === viewerRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (!pdfDocument || !canvasRef.current || !stageRef.current) return undefined;

    let isCancelled = false;

    const renderPage = async () => {
      setLoadStatus('rendering');
      const canvas = canvasRef.current;
      const stage = stageRef.current;
      if (!canvas || !stage) return;

      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      try {
        const page = await pdfDocument.getPage(activePage);
        if (isCancelled) return;

        const viewport = page.getViewport({ scale: 1 });
        const stageBounds = stage.getBoundingClientRect();
        const availableWidth = Math.max(stageBounds.width - 32, 240);
        const availableHeight = Math.max(stageBounds.height - 32, 180);
        const widthScale = availableWidth / viewport.width;
        const heightScale = availableHeight / viewport.height;
        const scale = isFullscreen ? Math.min(widthScale, heightScale) : widthScale;
        const scaledViewport = page.getViewport({ scale });
        const outputScale = Math.max(window.devicePixelRatio || 1, 1);
        const renderCanvas = document.createElement('canvas');
        const renderContext = renderCanvas.getContext('2d');

        renderCanvas.width = Math.floor(scaledViewport.width * outputScale);
        renderCanvas.height = Math.floor(scaledViewport.height * outputScale);
        renderContext.setTransform(outputScale, 0, 0, outputScale, 0, 0);
        renderContext.clearRect(0, 0, scaledViewport.width, scaledViewport.height);

        const renderTask = page.render({
          canvasContext: renderContext,
          viewport: scaledViewport,
        });
        renderTaskRef.current = renderTask;
        await renderTask.promise;

        if (!isCancelled) {
          const context = canvas.getContext('2d');

          canvas.width = renderCanvas.width;
          canvas.height = renderCanvas.height;
          canvas.style.width = `${Math.floor(scaledViewport.width)}px`;
          canvas.style.height = `${Math.floor(scaledViewport.height)}px`;

          context.setTransform(1, 0, 0, 1, 0, 0);
          context.clearRect(0, 0, canvas.width, canvas.height);
          context.drawImage(renderCanvas, 0, 0);
          page.cleanup();
          setHasRenderedSlide(true);
          setLoadStatus('ready');
        }
      } catch (error) {
        if (!isCancelled && error?.name !== 'RenderingCancelledException') {
          setLoadStatus('error');
        }
      } finally {
        if (!isCancelled) renderTaskRef.current = null;
      }
    };

    renderPage();

    const resizeObserver = new ResizeObserver(renderPage);
    resizeObserver.observe(stageRef.current);
    window.addEventListener('resize', renderPage);

    return () => {
      isCancelled = true;
      resizeObserver.disconnect();
      window.removeEventListener('resize', renderPage);
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [activePage, isFullscreen, pdfDocument]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isFullscreen) return;
      if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
        event.preventDefault();
        setActivePage((currentPage) => Math.max(currentPage - 1, 1));
      }
      if (event.key === 'ArrowRight' || event.key === 'PageDown' || event.key === ' ') {
        event.preventDefault();
        setActivePage((currentPage) => (
          pageCount ? Math.min(currentPage + 1, pageCount) : currentPage + 1
        ));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, pageCount]);

  const toggleFullscreen = async () => {
    const viewer = viewerRef.current;
    if (!viewer) return;

    try {
      if (document.fullscreenElement === viewer) {
        await document.exitFullscreen();
      } else if (viewer.requestFullscreen) {
        await viewer.requestFullscreen();
      }
    } catch (error) {
      // Fullscreen can be denied by the browser or embedding policy.
    }
  };

  return (
    <div
      className={`asset-pdf-presentation ${isFullscreen ? 'is-fullscreen' : ''}`}
      ref={viewerRef}
    >
      <button
        aria-label={isFullscreen ? 'Exit fullscreen presentation' : 'View presentation fullscreen'}
        className="asset-pdf-presentation-fullscreen-button"
        onClick={toggleFullscreen}
        title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        type="button"
      >
        {isFullscreen ? (
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M9 3v6H3" />
            <path d="m3 9 6-6" />
            <path d="M15 3v6h6" />
            <path d="m21 9-6-6" />
            <path d="M9 21v-6H3" />
            <path d="m3 15 6 6" />
            <path d="M15 21v-6h6" />
            <path d="m21 15-6 6" />
          </svg>
        ) : (
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="M3 9V3h6" />
            <path d="m3 3 7 7" />
            <path d="M21 9V3h-6" />
            <path d="m21 3-7 7" />
            <path d="M3 15v6h6" />
            <path d="m3 21 7-7" />
            <path d="M21 15v6h-6" />
            <path d="m21 21-7-7" />
          </svg>
        )}
      </button>
      <div className="asset-pdf-presentation-stage" ref={stageRef}>
        <canvas
          aria-label={`${title} presentation slide ${activePage}`}
          className="asset-pdf-presentation-canvas"
          ref={canvasRef}
        />
        {loadStatus !== 'ready' && !hasRenderedSlide && (
          <div className="asset-pdf-presentation-status">
            {loadStatus === 'error' ? 'PDF preview is unavailable.' : 'Loading slide...'}
          </div>
        )}
      </div>
      <div className="asset-pdf-presentation-toolbar">
        <span>Slide {activePage}{pageCount ? ` / ${pageCount}` : ''}</span>
        <div className="asset-pdf-presentation-controls" aria-label={`${title} slide controls`}>
          <button
            aria-label="Previous slide"
            disabled={activePage <= 1}
            onClick={() => setActivePage((currentPage) => Math.max(currentPage - 1, 1))}
            title="Previous slide"
            type="button"
          >
            <ChevronLeftIcon />
          </button>
          <button
            aria-label="Next slide"
            disabled={Boolean(pageCount && activePage >= pageCount)}
            onClick={() => setActivePage((currentPage) => (
              pageCount ? Math.min(currentPage + 1, pageCount) : currentPage + 1
            ))}
            title="Next slide"
            type="button"
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>
    </div>
  );
}

function AssetCard({
  addToCartDisabled = false,
  asset,
  assetCardBackgroundColor,
  assetCount = 1,
  assetFields,
  assetIndex = 0,
  cartButtonLabel = 'Add to cart',
  comboProductOptions = [],
  editable = false,
  enableHtmlEmbedFullscreen = false,
  enablePdfPresentation = false,
  isFirstAsset = false,
  isLastAsset = false,
  onAssetChange,
  onAssetDelete,
  onAssetMove,
  onAddToCart,
  onAddToItemForm,
  itemFormButtonText = 'Item form',
  onAddUpdate,
  onEnquire,
  onShare,
  permissions,
  link,
  listingType,
  showShareAction = true,
  shareDescription,
  shareTitle,
  shareUrl,
  showStoreQuantityHint = false,
}) {
  const [shareStatus, setShareStatus] = useState('');
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [fileChangeStatus, setFileChangeStatus] = useState('');
  const [galleryImageStatus, setGalleryImageStatus] = useState('');
  const [listingLogoStatus, setListingLogoStatus] = useState('');
  const [pdfScale, setPdfScale] = useState(1);
  const [selectedPreviewImageUrl, setSelectedPreviewImageUrl] = useState('');
  const [previewTileStartIndex, setPreviewTileStartIndex] = useState(0);
  const [isArticleExpanded, setIsArticleExpanded] = useState(false);
  const [hasArticleOverflow, setHasArticleOverflow] = useState(false);
  const [listItemsDraft, setListItemsDraft] = useState(() => shareListItemsToText(asset.listItems || []));
  const articlePreviewRef = useRef(null);
  const listItemsDraftAssetIdRef = useRef(asset.id);
  const assetUrl = asset.downloadUrl || asset.preview || '';
  const mediaPreviewUrl = asset.preview || asset.downloadUrl || '';
  const isVideo = isVideoAsset(asset);
  const isPdf = isPdfAsset(asset);
  const canRenderPdf = hasRenderablePdfSource(asset, assetUrl);
  const isImage = isImageAsset(asset);
  const imagePreviewUrl = isImage ? mediaPreviewUrl : '';
  const isHtmlEmbed = isHtmlEmbedAsset(asset);
  const isYouTubeVideo = isYouTubeVideoAsset(asset);
  const isProfileCard = isProfileCardAsset(asset);
  const isLinkInBio = isLinkInBioAsset(asset);
  const isSingleLinkInBioUrl = Boolean((isProfileCard || isLinkInBio) && asset.externalUrl);
  const isMarkdownArticle = isMarkdownArticleAsset(asset);
  const isForm = isFormAsset(asset);
  const isShareList = isShareListAsset(asset);
  const canDownload = Boolean(permissions?.canDownload && assetUrl);
  const canAddToCart = Boolean(onAddToCart && !editable);
  const canAddToItemForm = Boolean(onAddToItemForm && !editable);
  const enabledFields = { ...defaultAssetFields, ...(assetFields || {}) };
  const showCheckoutCostFields = Boolean(editable && showStoreQuantityHint);
  const comboProductIds = normaliseComboProductIds(asset.comboProductIds);
  const isComboAsset = comboProductIds.length > 0;
  const resolvedAssetCardBackgroundColor = isValidHexColor(assetCardBackgroundColor)
    ? assetCardBackgroundColor.trim()
    : '';
  const assetCardStyle = resolvedAssetCardBackgroundColor
    ? { backgroundColor: resolvedAssetCardBackgroundColor }
    : undefined;
  const canEnquire = Boolean(enabledFields.enquire && onEnquire && !editable);
  const canShare = Boolean(enabledFields.share && showShareAction && permissions?.canShare);
  const canAddUpdate = Boolean(enabledFields.updates && onAddUpdate && !editable);
  const availableSizes = normaliseOptionList(asset.availableSizes);
  const availableColors = normaliseOptionList(asset.availableColors);
  const itemImages = useMemo(() => (
    Array.isArray(asset.images) ? asset.images : []
  ), [asset.images]);
  const previewTileImages = useMemo(() => (
    itemImages
      .map((image) => ({
        ...image,
        imageUrl: imageUrlForItemImage(image),
      }))
      .filter((image) => image.imageUrl)
  ), [itemImages]);
  const visiblePreviewTileImages = previewTileImages.slice(previewTileStartIndex, previewTileStartIndex + 3);
  const maxPreviewTileStartIndex = Math.max(previewTileImages.length - 3, 0);
  const hasPreviewTileNavigation = previewTileImages.length > 3;
  const activeCardImageUrl = previewTileImages.some((image) => image.imageUrl === selectedPreviewImageUrl)
    ? selectedPreviewImageUrl
    : imagePreviewUrl;
  const showPreviewImageTiles = Boolean(enabledFields.multipleImages && imagePreviewUrl && visiblePreviewTileImages.length);
  const hasDescription = Boolean(enabledFields.description && asset.description && !isLinkInBio && !isProfileCard && !isShareList);
  const hasStoreBasePrice = Boolean(String(asset.storeBasePrice || '').trim());
  const effectivePrice = isComboAsset || hasStoreBasePrice ? parsePrice(asset.price) : getEffectivePrice(asset);
  const hasReducedPrice = isComboAsset
    ? parsePrice(asset.comboBasePrice) > 0 && parsePrice(asset.price) < parsePrice(asset.comboBasePrice)
    : hasStoreBasePrice
      ? parsePrice(asset.storeBasePrice) > 0 && parsePrice(asset.price) < parsePrice(asset.storeBasePrice)
      : hasDiscountedPrice(asset);
  const hasPrice = Boolean(enabledFields.price && asset.price);
  const hasFlag = Boolean(!editable && String(asset.flag || '').trim());
  const hasPrepay = Boolean(!editable && String(asset.prepay || '').trim());
  const hasQuantity = Boolean(enabledFields.quantity && asset.quantity);
  const hasArea = Boolean(enabledFields.area && String(asset.area || '').trim());
  const hasSizes = Boolean(enabledFields.sizes && availableSizes.length);
  const hasColors = Boolean(enabledFields.colors && availableColors.length);
  const hasCategory = Boolean(String(asset.category || '').trim());
  const listingSpecs = listingType ? listingSpecsForAsset(asset, listingType) : [];
  const hasListingSpecs = Boolean(listingSpecs.length);
  const hasInlineMeta = hasPrice || hasQuantity || hasArea || hasListingSpecs;
  const assetLinkUrl = normaliseAssetLink(asset.linkUrl || asset.externalUrl || '');
  const canOpenAssetLink = Boolean(enabledFields.link && assetLinkUrl);
  const priceLabel = formatPrice(asset.currency || 'ZAR', effectivePrice, asset.price);
  const originalPriceSource = isComboAsset ? asset.comboBasePrice : hasStoreBasePrice ? asset.storeBasePrice : asset.price;
  const originalPriceLabel = formatPrice(asset.currency || 'ZAR', originalPriceSource, originalPriceSource);
  const pricePrefix = String(asset.pricePrefix || '').trim();
  const isActive = asset.active !== false;
  const canViewDetail = Boolean(enabledFields.details && !editable);
  const showMediaDetailOverlay = Boolean(canViewDetail && mediaPreviewUrl && (isImage || isVideo));
  const hasAssetActions = canViewDetail || canAddToCart || canAddToItemForm || canAddUpdate || canEnquire || canDownload || canShare;
  const hasPublicSummary = !editable && !isShareList && (enabledFields.title || hasInlineMeta);
  const hasPublicDescription = !editable && hasDescription;
  const hasCardBodyContent = editable || hasPublicSummary || hasPublicDescription || hasAssetActions || shareStatus;
  const youtubeEmbedUrl = asset.youtubeEmbedUrl || buildYouTubeEmbedUrl(asset.youtubeVideoId);
  const pdfPreviewUrl = isPdf && canRenderPdf ? buildPdfPreviewUrl(assetUrl) : '';
  const canChangeFile = editable && !isHtmlEmbed && !isYouTubeVideo && !isLinkInBio && !isProfileCard && !isMarkdownArticle && !isForm && !isShareList;
  const shouldClampArticlePreview = Boolean(isMarkdownArticle && assetCount > 1);

  const updateAssetField = (field, value) => {
    onAssetChange?.({
      ...asset,
      [field]: value,
    });
  };

  const updateComboCalculation = ({
    discountType = asset.discountType || 'percentage',
    discountValue = asset.discountValue || '',
    productIds = comboProductIds,
  } = {}) => {
    const comboAmounts = comboAmountsForProducts(productIds, comboProductOptions, discountType, discountValue);
    onAssetChange?.({
      ...asset,
      comboProductIds: productIds,
      comboBasePrice: formatComboAmount(comboAmounts.basePrice, comboAmounts.hasProducts),
      price: formatComboAmount(comboAmounts.price, comboAmounts.hasProducts),
      unitCost: formatComboAmount(comboAmounts.unitCost, comboAmounts.hasProducts),
      profit: formatComboAmount(comboAmounts.profit, comboAmounts.hasProducts),
      discountType,
      discountValue,
      reducedPrice: '',
    });
  };

  const updateComboProducts = (nextProductIds) => {
    updateComboCalculation({ productIds: nextProductIds });
  };

  const updateStorePricingField = (field, value) => {
    const nextAsset = {
      ...asset,
      [field]: value,
      ...(field === 'price' ? { discountValue: '', reducedPrice: '', storeBasePrice: '' } : {}),
    };

    onAssetChange?.({
      ...nextAsset,
      profit: profitForAsset(nextAsset),
    });
  };

  const updateDiscountField = (field, value) => {
    if (!isComboAsset) {
      const discountType = field === 'discountType' ? value : asset.discountType || 'percentage';
      const discountValue = field === 'discountValue' ? value : asset.discountValue || '';
      const basePrice = basePriceForStoreAsset(asset);
      const nextPrice = discountValue === ''
        ? parsePrice(basePrice)
        : calculateDiscountedPrice(basePrice, discountType, discountValue);
      const nextAsset = {
        ...asset,
        discountType,
        discountValue,
        storeBasePrice: discountValue === '' ? '' : basePrice,
        price: formatCalculatedAmount(nextPrice, String(basePrice || discountValue).trim() !== ''),
        reducedPrice: '',
      };

      onAssetChange?.({
        ...nextAsset,
        profit: profitForAsset(nextAsset),
      });
      return;
    }

    updateComboCalculation({
      discountType: field === 'discountType' ? value : asset.discountType || 'percentage',
      discountValue: field === 'discountValue' ? value : asset.discountValue || '',
    });
  };

  const updateYouTubeUrl = (value) => {
    const youtubeVideoId = getYouTubeVideoId(value);

    onAssetChange?.({
      ...asset,
      youtubeUrl: value,
      youtubeVideoId,
      youtubeEmbedUrl: buildYouTubeEmbedUrl(youtubeVideoId),
    });
  };

  const uploadRichContentImage = async (file) => {
    if (!link?.id) {
      throw new Error('Save the link before uploading rich content images.');
    }

    const uploadFile = await optimiseImageFile(file);
    const upload = await storageService.uploadAssetFile(link.id, uploadFile);
    return upload.downloadUrl;
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setFileChangeStatus(file.type.startsWith('image/') ? 'Optimising replacement image...' : '');

    let nextFile = file;
    try {
      nextFile = file.type.startsWith('image/') ? await optimiseImageFile(file) : file;
    } catch (error) {
      setFileChangeStatus(error.message || 'Unable to optimise image. Using original file.');
      nextFile = file;
    }

    const nextPreview = previewForFile(nextFile);

    onAssetChange?.({
      ...asset,
      file: nextFile,
      contentType: nextFile.type || 'application/octet-stream',
      downloadUrl: asset.downloadUrl || '',
      name: nextFile.name,
      preview: nextPreview,
      shape: assetShapeForFile(nextFile),
      size: formatFileSize(nextFile.size),
      status: 'Draft',
      storagePath: '',
      type: assetTypeForFile(nextFile),
      uploadBytes: nextFile.size,
    });

    if (nextFile !== file) {
      setFileChangeStatus(`Image optimised to JPEG (${formatFileSize(nextFile.size)}). Save this link to upload it.`);
    } else if (!file.type.startsWith('image/')) {
      setFileChangeStatus('');
    }
  };

  const handleGalleryImagesChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;

    setGalleryImageStatus('Optimising item images...');

    try {
      const nextImages = await Promise.all(files.map(async (file, index) => {
        const optimisedFile = await optimiseImageFile(file);
        return {
          id: `item-image-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
          name: optimisedFile.name,
          preview: URL.createObjectURL(optimisedFile),
          contentType: optimisedFile.type || 'image/jpeg',
          uploadBytes: optimisedFile.size,
          file: optimisedFile,
        };
      }));

      updateAssetField('images', [...itemImages, ...nextImages]);
      setGalleryImageStatus(`${nextImages.length} image${nextImages.length === 1 ? '' : 's'} added. Save this link to upload.`);
    } catch (error) {
      setGalleryImageStatus(error.message || 'Unable to add item images.');
    }
  };

  const removeGalleryImage = (imageId) => {
    updateAssetField('images', itemImages.filter((image) => image.id !== imageId));
  };

  const handleListingLogoChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setListingLogoStatus('Optimising listing logo...');
    try {
      const optimisedFile = await optimiseImageFile(file);
      onAssetChange?.({
        ...asset,
        listingLogoFile: optimisedFile,
        listingLogoUrl: URL.createObjectURL(optimisedFile),
      });
      setListingLogoStatus('Listing logo optimised. Save this link to upload it.');
    } catch (error) {
      setListingLogoStatus(error.message || 'Unable to optimise listing logo.');
    }
  };

  const trackShareAction = () => {
    Promise.resolve(onShare?.()).catch((error) => {
      console.warn('Unable to track asset share.', error);
    });
  };

  const handleShare = async () => {
    const url = buildAssetShareUrl(shareUrl || window.location.href, asset);
    const shareData = {
      title: asset.title || shareTitle || 'Sharabl asset link',
      text: asset.description || shareDescription || asset.title || 'Controlled branded links for documents, photos, albums, and product catalogs.',
      url,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        setShareStatus('');
        trackShareAction();
        return;
      }

      await navigator.clipboard.writeText(url);
      setShareStatus('Link copied.');
      trackShareAction();
    } catch (error) {
      if (error.name !== 'AbortError') {
        setShareStatus('Unable to share.');
      }
    }
  };

  const updatePdfScale = (nextScale) => {
    setPdfScale(Math.min(Math.max(nextScale, 0.75), 1.5));
  };

  useEffect(() => {
    if (!isDetailOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsDetailOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDetailOpen]);

  useEffect(() => {
    setSelectedPreviewImageUrl((currentImageUrl) => (
      currentImageUrl && previewTileImages.some((image) => image.imageUrl === currentImageUrl)
        ? currentImageUrl
        : ''
    ));
  }, [imagePreviewUrl, previewTileImages]);

  useEffect(() => {
    setPreviewTileStartIndex((currentIndex) => Math.min(currentIndex, maxPreviewTileStartIndex));
  }, [maxPreviewTileStartIndex]);

  useEffect(() => {
    if (!shouldClampArticlePreview) {
      setIsArticleExpanded(false);
      setHasArticleOverflow(false);
      return undefined;
    }

    const measureArticleOverflow = () => {
      const articleElement = articlePreviewRef.current;
      if (!articleElement) return;

      const computedStyle = window.getComputedStyle(articleElement);
      const lineHeight = Number.parseFloat(computedStyle.lineHeight) || 28;
      const paddingBlock = (
        (Number.parseFloat(computedStyle.paddingTop) || 0) +
        (Number.parseFloat(computedStyle.paddingBottom) || 0)
      );
      const maxCollapsedHeight = (lineHeight * 16) + paddingBlock;
      setHasArticleOverflow(articleElement.scrollHeight > maxCollapsedHeight + 1);
    };

    measureArticleOverflow();
    window.addEventListener('resize', measureArticleOverflow);
    return () => window.removeEventListener('resize', measureArticleOverflow);
  }, [asset.articleMarkdown, shouldClampArticlePreview]);

  useEffect(() => {
    if (listItemsDraftAssetIdRef.current === asset.id) return;
    listItemsDraftAssetIdRef.current = asset.id;
    setListItemsDraft(shareListItemsToText(asset.listItems || []));
  }, [asset.id, asset.listItems]);

  const renderDetailPreview = () => {
    if (isHtmlEmbed) {
      return (
        <HtmlEmbedFrame
          enableFullscreen={enableHtmlEmbedFullscreen}
          html={asset.embedHtml || ''}
          title={`${asset.title || 'HTML embed'} detail preview`}
        />
      );
    }

    if (isYouTubeVideo) {
      return youtubeEmbedUrl ? (
        <iframe
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="asset-youtube-frame"
          src={youtubeEmbedUrl}
          title={`${asset.title || 'YouTube video'} detail preview`}
        />
      ) : (
        <span>Invalid YouTube URL</span>
      );
    }

    if (isMarkdownArticle) {
      return <MarkdownArticle>{asset.articleMarkdown}</MarkdownArticle>;
    }

    if (isForm) {
      return (
        <ShareFormRenderer
          asset={asset}
          className="border-0 p-0 shadow-none"
          link={link}
        />
      );
    }

    if (isShareList) {
      return <ShareListAsset asset={asset} />;
    }

    if (mediaPreviewUrl && isVideo) {
      return (
        <video
          aria-label={`${asset.title || 'Video'} detail preview`}
          controls
          playsInline
          preload="metadata"
          src={mediaPreviewUrl}
        />
      );
    }

    if (isPdf && canRenderPdf) {
      return (
        <object
          aria-label={`${asset.title || 'PDF'} detail preview`}
          className="asset-detail-pdf-object"
          data={pdfPreviewUrl}
          type="application/pdf"
        >
          <a
            className="grid h-48 place-items-center px-4 text-center text-sm font-semibold text-slate-700"
            href={assetUrl}
            rel="noreferrer"
            target="_blank"
          >
            Open PDF preview
          </a>
        </object>
      );
    }

    if (imagePreviewUrl) {
      return <img src={imagePreviewUrl} alt={`${asset.title || 'Asset'} detail preview`} />;
    }

    return <span>{displayAssetType(asset)}</span>;
  };

  return (
    <>
    <article
      className={`asset-card ${mediaPreviewUrl || isPdf || isHtmlEmbed || isYouTubeVideo || isProfileCard || isLinkInBio || isMarkdownArticle || isForm || isShareList ? 'has-preview' : ''} ${isProfileCard ? 'profile-card-asset' : asset.shape || 'standard'} ${isPdf && enablePdfPresentation ? 'presentation-pdf' : ''} ${isActive ? '' : 'opacity-60 ring-1 ring-slate-300'}`}
      style={assetCardStyle}
    >
      <div className={`asset-preview relative bg-gradient-to-br ${asset.color || 'from-white to-slate-50'} ${showPreviewImageTiles ? 'has-image-tiles' : ''}`}>
        {isHtmlEmbed ? (
          <HtmlEmbedFrame
            enableFullscreen={enableHtmlEmbedFullscreen}
            html={asset.embedHtml || ''}
            title={`${asset.title || 'HTML embed'} preview`}
          />
        ) : isYouTubeVideo ? (
          youtubeEmbedUrl ? (
            <iframe
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="asset-youtube-frame"
              src={youtubeEmbedUrl}
              title={`${asset.title || 'YouTube video'} preview`}
            />
          ) : (
            <span>Invalid YouTube URL</span>
          )
        ) : isProfileCard ? (
          <ProfileCardAsset asset={asset} assetLinkUrl={assetLinkUrl} />
        ) : isLinkInBio ? (
          <div className={`asset-link-bio ${asset.isPrimaryAction ? 'primary' : ''}`}>
            {isSingleLinkInBioUrl ? (
              <>
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    {asset.section && <p className="label-caps">{asset.section}</p>}
                    <h3 className="mt-2 text-lg font-semibold leading-snug text-slate-950">{asset.title}</h3>
                  </div>
                  {asset.isPrimaryAction && <span className="asset-link-bio-badge">Featured</span>}
                </div>
                {asset.description && (
                  <div className="asset-description mt-3 text-sm text-slate-600">
                    <ReactMarkdown>{asset.description}</ReactMarkdown>
                  </div>
                )}
                <a
                  className="asset-link-bio-item mt-4"
                  href={assetLinkUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  {asset.ctaLabel || 'Open profile'}
                </a>
              </>
            ) : (
              <>
                {asset.description && (
                  <div className="asset-description text-sm text-slate-600">
                    <ReactMarkdown>{asset.description}</ReactMarkdown>
                  </div>
                )}
                <div className="mt-4 grid gap-2">
                  {(asset.linkItems || []).map((item, index) => (
                    <a
                      className="asset-link-bio-item"
                      href={normaliseAssetLink(item.url)}
                      key={`${item.url}-${index}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {item.label || item.url}
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>
        ) : isMarkdownArticle ? (
          <div className="asset-markdown-card-preview">
            <MarkdownArticle
              articleRef={articlePreviewRef}
              className={shouldClampArticlePreview && !isArticleExpanded ? 'asset-markdown-preview-clamp' : ''}
            >
              {asset.articleMarkdown}
            </MarkdownArticle>
            {shouldClampArticlePreview && hasArticleOverflow && (
              <button
                className="asset-markdown-toggle-button"
                onClick={() => setIsArticleExpanded((current) => !current)}
                type="button"
              >
                {isArticleExpanded ? 'Show Less' : 'Show More'}
              </button>
            )}
          </div>
        ) : isForm ? (
          <ShareFormRenderer
            asset={asset}
            className="asset-form-renderer"
            link={link}
          />
        ) : isShareList ? (
          <ShareListAsset asset={asset} />
        ) : mediaPreviewUrl && isVideo ? (
          <video
            aria-label={`${asset.title} preview`}
            controls
            playsInline
            preload="metadata"
            src={mediaPreviewUrl}
          />
        ) : isPdf && canRenderPdf && enablePdfPresentation ? (
          <PdfPresentationViewer asset={asset} assetUrl={assetUrl} />
        ) : isPdf && canRenderPdf ? (
          <div className="asset-pdf-preview">
            <div className="asset-pdf-toolbar">
              <span>A4 preview</span>
              <div className="asset-pdf-controls" aria-label={`${asset.title || 'PDF'} preview controls`}>
                <button
                  aria-label="Reduce PDF preview size"
                  disabled={pdfScale <= 0.75}
                  onClick={() => updatePdfScale(pdfScale - 0.25)}
                  title="Reduce PDF preview size"
                  type="button"
                >
                  -
                </button>
                <span>{Math.round(pdfScale * 100)}%</span>
                <button
                  disabled={pdfScale === 1}
                  onClick={() => updatePdfScale(1)}
                  type="button"
                >
                  Reset
                </button>
                <button
                  aria-label="Increase PDF preview size"
                  disabled={pdfScale >= 1.5}
                  onClick={() => updatePdfScale(pdfScale + 0.25)}
                  title="Increase PDF preview size"
                  type="button"
                >
                  +
                </button>
              </div>
            </div>
            <div
              className="asset-pdf-document-shell"
              style={{
                maxWidth: `${Math.round(794 * pdfScale)}px`,
              }}
            >
              <object
                aria-label={`${asset.title} PDF preview`}
                className="asset-pdf-object"
                data={pdfPreviewUrl}
                key={pdfPreviewUrl}
                type="application/pdf"
              >
                <a
                  className="grid h-48 place-items-center px-4 text-center text-sm font-semibold text-slate-700"
                  href={assetUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  Open PDF preview
                </a>
              </object>
            </div>
          </div>
        ) : imagePreviewUrl ? (
          showPreviewImageTiles ? (
            <div className="asset-card-image-stack">
              <img src={activeCardImageUrl} alt={`${asset.title || 'Asset'} preview`} />
              <div className={`asset-card-image-tile-row ${hasPreviewTileNavigation ? 'has-nav' : ''}`}>
                {hasPreviewTileNavigation && (
                  <button
                    aria-label="Show previous additional images"
                    className="asset-card-image-nav previous"
                    disabled={previewTileStartIndex === 0}
                    onClick={() => setPreviewTileStartIndex((currentIndex) => Math.max(currentIndex - 3, 0))}
                    type="button"
                  >
                    <ChevronLeftIcon />
                  </button>
                )}
                <div className="asset-card-image-tiles" aria-label={`${asset.title || 'Asset'} additional images`}>
                  {visiblePreviewTileImages.map((image) => (
                    <button
                      aria-label={`Show ${image.name || 'additional image'} as main image`}
                      aria-pressed={activeCardImageUrl === image.imageUrl}
                      className={activeCardImageUrl === image.imageUrl ? 'active' : ''}
                      key={image.id || image.imageUrl}
                      onClick={() => setSelectedPreviewImageUrl(image.imageUrl)}
                      type="button"
                    >
                      <img src={image.imageUrl} alt={image.name || `${asset.title || 'Asset'} additional image`} />
                    </button>
                  ))}
                </div>
                {hasPreviewTileNavigation && (
                  <button
                    aria-label="Show next additional images"
                    className="asset-card-image-nav next"
                    disabled={previewTileStartIndex >= maxPreviewTileStartIndex}
                    onClick={() => setPreviewTileStartIndex((currentIndex) => Math.min(currentIndex + 3, maxPreviewTileStartIndex))}
                    type="button"
                  >
                    <ChevronRightIcon />
                  </button>
                )}
              </div>
            </div>
          ) : (
            <img src={imagePreviewUrl} alt={`${asset.title || 'Asset'} preview`} />
          )
        ) : (
          <span>{displayAssetType(asset)}</span>
        )}
        {canOpenAssetLink && (
          <a
            aria-label={`Open ${asset.title || 'asset'} link`}
            className="absolute bottom-3 right-3 z-10 grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white/50 text-slate-700 shadow-sm transition hover:border-slate-950 hover:text-slate-950"
            href={assetLinkUrl}
            rel="noreferrer"
            target="_blank"
            title="Open profile"
          >
            <LinkIcon />
          </a>
        )}
        {showMediaDetailOverlay && (
          <button
            aria-label={`View details for ${asset.title || 'asset'}`}
            className="asset-detail-overlay-button"
            onClick={() => setIsDetailOpen(true)}
            title="View details"
            type="button"
          >
            <EyeIcon />
          </button>
        )}
        {!editable && hasReducedPrice && (
          <span className="asset-sale-flag">SALE</span>
        )}
        {hasFlag && (
          <span className="asset-image-flag">{asset.flag}</span>
        )}
      </div>
      {hasCardBodyContent && (
      <div className="p-4">
        {editable && (
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-slate-950">Edit asset</p>
                {!isActive && <span className="chip">Inactive</span>}
              </div>
              <p className="asset-item-name mt-1 text-xs text-slate-500">{asset.name || asset.title || 'Uploaded file'}</p>
            </div>
            <div className="flex shrink-0 flex-wrap justify-end gap-2">
              <button
                aria-label="Move asset up"
                className="grid h-9 w-9 place-items-center rounded border border-slate-200 bg-white text-lg font-semibold leading-none text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-40"
                disabled={isFirstAsset}
                onClick={() => onAssetMove?.(assetIndex, assetIndex - 1)}
                title="Move asset up"
                type="button"
              >
                &uarr;
              </button>
              <button
                aria-label="Move asset down"
                className="grid h-9 w-9 place-items-center rounded border border-slate-200 bg-white text-lg font-semibold leading-none text-slate-700 transition hover:border-slate-950 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-40"
                disabled={isLastAsset}
                onClick={() => onAssetMove?.(assetIndex, assetIndex + 1)}
                title="Move asset down"
                type="button"
              >
                &darr;
              </button>
              <button
                className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 transition hover:border-red-300 hover:bg-red-100"
                onClick={() => onAssetDelete?.(asset)}
                type="button"
              >
                Delete
              </button>
            </div>
          </div>
        )}
        {editable && (
          <label
            className="mb-3 flex items-center justify-between gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700"
            htmlFor={`asset-active-${asset.id}`}
          >
            <span>{isActive ? 'Active on public page' : 'Hidden from public page'}</span>
            <input
              checked={isActive}
              id={`asset-active-${asset.id}`}
              onChange={(event) => updateAssetField('active', event.target.checked)}
              type="checkbox"
            />
          </label>
        )}
        {editable && enabledFields.title ? (
          <div>
            <label className="field-label mt-0" htmlFor={`asset-title-${asset.id}`}>Asset name</label>
            <input
              id={`asset-title-${asset.id}`}
              className="field"
              value={asset.title || ''}
              onChange={(event) => updateAssetField('title', event.target.value)}
            />
          </div>
        ) : !editable && (enabledFields.title || hasInlineMeta) ? (
          <div className="grid min-w-0 gap-2">
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-2">
              {enabledFields.title && <h2 className="asset-item-name text-base font-semibold leading-snug">{asset.title}</h2>}
              {(hasCategory || hasQuantity || hasArea) && (
                <div className="flex flex-wrap gap-2">
                  {hasCategory && <span className="chip asset-meta-chip">{asset.category}</span>}
                  {hasQuantity && <span className="chip asset-meta-chip">Qty: {asset.quantity}</span>}
                  {hasArea && <span className="chip asset-meta-chip">{asset.area}</span>}
                </div>
              )}
            </div>
            {hasListingSpecs && <ListingSpecs specs={listingSpecs} />}
            {hasPrice && (
              <div className="flex flex-wrap gap-2">
                <span className="asset-price-badge">
                  {hasReducedPrice && <span className="asset-price-original">{originalPriceLabel}</span>}
                  {pricePrefix && <span className="asset-price-prefix">{pricePrefix}</span>}
                  <span className="asset-price-current">{priceLabel}</span>
                  {hasPrepay && <span className="asset-prepay-label">{asset.prepay}</span>}
                </span>
              </div>
            )}
          </div>
        ) : null}
        {!editable && (hasSizes || hasColors) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {hasSizes && <span className="chip">Sizes: {availableSizes.join(', ')}</span>}
            {hasColors && <span className="chip">Colors: {availableColors.join(', ')}</span>}
          </div>
        )}
        {editable && (
          <div className="mt-3 flex flex-wrap gap-2">
            {hasCategory && <span className="chip">{asset.category}</span>}
            {hasArea && <span className="chip">{asset.area}</span>}
            <span className="chip">{asset.size}</span>
            <span className="chip">{asset.status}</span>
          </div>
        )}
        {editable && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <label className="field-label mt-0" htmlFor={`asset-category-${asset.id}`}>Category</label>
            <input
              className="field"
              id={`asset-category-${asset.id}`}
              placeholder="e.g. Featured, Documents, Training"
              value={asset.category || ''}
              onChange={(event) => updateAssetField('category', event.target.value)}
            />
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="field-label mt-0" htmlFor={`asset-source-${asset.id}`}>Source</label>
                <input
                  className="field"
                  id={`asset-source-${asset.id}`}
                  placeholder="Internal source or supplier"
                  value={asset.source || ''}
                  onChange={(event) => updateAssetField('source', event.target.value)}
                />
              </div>
              <div>
                <label className="field-label mt-0" htmlFor={`asset-flag-${asset.id}`}>Flag</label>
                <input
                  className="field"
                  id={`asset-flag-${asset.id}`}
                  placeholder="e.g. New, Featured, Limited"
                  value={asset.flag || ''}
                  onChange={(event) => updateAssetField('flag', event.target.value)}
                />
              </div>
            </div>
          </div>
        )}
        {editable && listingType && (
          <ListingFieldsEditor
            asset={asset}
            listingType={listingType}
            onChange={onAssetChange}
          />
        )}
        {editable && listingType && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <label className="field-label mt-0" htmlFor={`asset-listing-logo-${asset.id}`}>Listing logo</label>
            <div className="flex flex-wrap items-center gap-3">
              <label className="asset-listing-logo-upload" htmlFor={`asset-listing-logo-${asset.id}`}>
                {asset.listingLogoUrl ? (
                  <img src={asset.listingLogoUrl} alt="Listing logo preview" />
                ) : (
                  <span>Upload logo</span>
                )}
              </label>
              <label
                className="cursor-pointer rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                htmlFor={`asset-listing-logo-${asset.id}`}
              >
                Choose logo
              </label>
              <input
                accept="image/*"
                className="sr-only"
                id={`asset-listing-logo-${asset.id}`}
                onChange={handleListingLogoChange}
                type="file"
              />
            </div>
            {listingLogoStatus && (
              <p className="mt-2 text-xs font-semibold text-slate-600">{listingLogoStatus}</p>
            )}
          </div>
        )}
        {editable && enabledFields.area && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <label className="field-label mt-0" htmlFor={`asset-area-${asset.id}`}>Area</label>
            <input
              className="field"
              id={`asset-area-${asset.id}`}
              placeholder="e.g. Camps Bay, Sandton, Umhlanga"
              value={asset.area || ''}
              onChange={(event) => updateAssetField('area', event.target.value)}
            />
          </div>
        )}
        {editable && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <label className="field-label mt-0" htmlFor={`asset-images-${asset.id}`}>Additional images</label>
            <label
              className="inline-flex cursor-pointer rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
              htmlFor={`asset-images-${asset.id}`}
            >
              Add images
            </label>
            <input
              accept="image/*"
              className="sr-only"
              id={`asset-images-${asset.id}`}
              multiple
              onChange={handleGalleryImagesChange}
              type="file"
            />
            {itemImages.length > 0 && (
              <div className="asset-item-image-manager">
                {itemImages.map((image) => {
                  const imageUrl = imageUrlForItemImage(image);
                  return (
                    <div className="asset-item-image-thumb" key={image.id || imageUrl || image.name}>
                      {imageUrl ? <img src={imageUrl} alt={image.name || 'Item image'} /> : <span>Image</span>}
                      <button
                        aria-label="Remove item image"
                        onClick={() => removeGalleryImage(image.id)}
                        type="button"
                      >
                        x
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {galleryImageStatus && (
              <p className="mt-2 text-xs font-semibold text-slate-600">
                {galleryImageStatus}
              </p>
            )}
          </div>
        )}
        {canChangeFile && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <label className="field-label mt-0" htmlFor={`asset-file-${asset.id}`}>File</label>
            <div className="flex flex-wrap items-center gap-2">
              <label
                className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                htmlFor={`asset-file-${asset.id}`}
              >
                Change file
              </label>
              <span className="asset-item-name min-w-0 text-xs text-slate-500">
                {asset.name || asset.title || 'Uploaded file'}
              </span>
            </div>
            <input
              className="sr-only"
              id={`asset-file-${asset.id}`}
              onChange={handleFileChange}
              type="file"
            />
            {asset.file && (
              <p className="mt-2 text-xs font-semibold text-amber-700">
                Replacement selected. Save this link to upload the new file.
              </p>
            )}
            {fileChangeStatus && (
              <p className="mt-2 text-xs font-semibold text-slate-600">
                {fileChangeStatus}
              </p>
            )}
          </div>
        )}
        {editable && enabledFields.link && !isSingleLinkInBioUrl && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <label className="field-label mt-0" htmlFor={`asset-link-${asset.id}`}>Link</label>
            <input
              id={`asset-link-${asset.id}`}
              className="field"
              placeholder="https://example.com"
              value={asset.linkUrl || ''}
              onChange={(event) => updateAssetField('linkUrl', event.target.value)}
            />
          </div>
        )}
        {editable && isHtmlEmbed && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <label className="field-label mt-0" htmlFor={`asset-embed-${asset.id}`}>Embed HTML</label>
            <textarea
              id={`asset-embed-${asset.id}`}
              className="field min-h-40 font-mono text-xs"
              placeholder="<iframe ...></iframe>"
              value={asset.embedHtml || ''}
              onChange={(event) => updateAssetField('embedHtml', event.target.value)}
            />
          </div>
        )}
        {editable && isYouTubeVideo && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <label className="field-label mt-0" htmlFor={`asset-youtube-${asset.id}`}>YouTube URL</label>
            <input
              id={`asset-youtube-${asset.id}`}
              className="field"
              placeholder="https://www.youtube.com/watch?v=..."
              value={asset.youtubeUrl || ''}
              onChange={(event) => updateYouTubeUrl(event.target.value)}
            />
            {!youtubeEmbedUrl && (
              <p className="mt-2 text-xs font-semibold text-red-600">
                Paste a valid YouTube watch, Shorts, embed, or youtu.be URL.
              </p>
            )}
          </div>
        )}
        {editable && isSingleLinkInBioUrl && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <label className="field-label mt-0" htmlFor={`asset-url-${asset.id}`}>Main profile URL</label>
            <input
              id={`asset-url-${asset.id}`}
              className="field"
              placeholder="https://example.com"
              value={asset.externalUrl || ''}
              onChange={(event) => updateAssetField('externalUrl', event.target.value)}
            />
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="field-label mt-0" htmlFor={`asset-cta-${asset.id}`}>Primary action</label>
                <input
                  id={`asset-cta-${asset.id}`}
                  className="field"
                  placeholder="Open profile"
                  value={asset.ctaLabel || ''}
                  onChange={(event) => updateAssetField('ctaLabel', event.target.value)}
                />
              </div>
              <div>
                <label className="field-label mt-0" htmlFor={`asset-section-${asset.id}`}>Profile type</label>
                <input
                  id={`asset-section-${asset.id}`}
                  className="field"
                  placeholder="Personal"
                  value={asset.section || ''}
                  onChange={(event) => updateAssetField('section', event.target.value)}
                />
              </div>
            </div>
            <label className="field-label" htmlFor={`asset-tagline-${asset.id}`}>Tagline</label>
            <input
              id={`asset-tagline-${asset.id}`}
              className="field"
              placeholder="Certified specialist serving Durban"
              value={asset.tagline || ''}
              onChange={(event) => updateAssetField('tagline', event.target.value)}
            />
            <label className="field-label" htmlFor={`asset-experience-${asset.id}`}>Experience</label>
            <textarea
              id={`asset-experience-${asset.id}`}
              className="field min-h-20"
              placeholder="10 years supporting residential and commercial clients"
              value={asset.experience || ''}
              onChange={(event) => updateAssetField('experience', event.target.value)}
            />
            <OptionTagEditor
              inputId={`asset-qualifications-${asset.id}`}
              label="Qualifications / Accreditations"
              onChange={(nextOptions) => updateAssetField('qualifications', nextOptions)}
              placeholder="Trade certificate"
              value={asset.qualifications}
            />
            <OptionTagEditor
              inputId={`asset-profile-tags-${asset.id}`}
              label="Tags"
              onChange={(nextOptions) => updateAssetField('tags', nextOptions)}
              placeholder="Emergency repairs"
              value={asset.tags}
            />
            <label className="field-label" htmlFor={`asset-icon-${asset.id}`}>Profile image or logo</label>
            <label className="upload-tile aspect-[5/2]">
              {asset.iconUrl ? (
                <img src={asset.iconUrl} alt="Link icon preview" />
              ) : (
                <span>Upload icon</span>
              )}
              <input
                id={`asset-icon-${asset.id}`}
                type="file"
                accept="image/*"
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (!file) return;
                  try {
                    const optimisedFile = await optimiseImageFile(file);
                    onAssetChange?.({
                      ...asset,
                      iconFile: optimisedFile,
                      iconUrl: URL.createObjectURL(optimisedFile),
                    });
                  } catch (error) {
                    setFileChangeStatus(error.message || 'Unable to optimise image.');
                  }
                }}
              />
            </label>
            <label
              className="mt-3 flex items-center justify-between gap-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700"
              htmlFor={`asset-primary-${asset.id}`}
            >
              <span>Primary action</span>
              <input
                checked={Boolean(asset.isPrimaryAction)}
                id={`asset-primary-${asset.id}`}
                onChange={(event) => updateAssetField('isPrimaryAction', event.target.checked)}
                type="checkbox"
              />
            </label>
          </div>
        )}
        {editable && isShareList && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="field-label mt-0" htmlFor={`asset-list-action-label-${asset.id}`}>List action</label>
                <input
                  id={`asset-list-action-label-${asset.id}`}
                  className="field"
                  placeholder="Open register"
                  value={asset.actionLabel || ''}
                  onChange={(event) => updateAssetField('actionLabel', event.target.value)}
                />
              </div>
              <div>
                <label className="field-label mt-0" htmlFor={`asset-list-action-url-${asset.id}`}>Action URL</label>
                <input
                  id={`asset-list-action-url-${asset.id}`}
                  className="field"
                  placeholder="https://example.com"
                  value={asset.actionUrl || ''}
                  onChange={(event) => updateAssetField('actionUrl', event.target.value)}
                />
              </div>
            </div>
            <OptionTagEditor
              inputId={`asset-list-tags-${asset.id}`}
              label="Tags"
              onChange={(nextOptions) => updateAssetField('tags', nextOptions)}
              placeholder="Term 2"
              value={asset.tags}
            />
            <label className="field-label" htmlFor={`asset-list-image-${asset.id}`}>List image or logo</label>
            <label className="upload-tile aspect-[5/2]">
              {asset.iconUrl ? (
                <img src={asset.iconUrl} alt="List preview" />
              ) : (
                <span>Upload image</span>
              )}
              <input
                id={`asset-list-image-${asset.id}`}
                type="file"
                accept="image/*"
                onChange={async (event) => {
                  const file = event.target.files?.[0];
                  event.target.value = '';
                  if (!file) return;
                  try {
                    const optimisedFile = await optimiseImageFile(file);
                    onAssetChange?.({
                      ...asset,
                      iconFile: optimisedFile,
                      iconUrl: URL.createObjectURL(optimisedFile),
                    });
                  } catch (error) {
                    setFileChangeStatus(error.message || 'Unable to optimise image.');
                  }
                }}
              />
            </label>
            <label className="field-label" htmlFor={`asset-list-items-${asset.id}`}>Items</label>
            <textarea
              id={`asset-list-items-${asset.id}`}
              className="field min-h-48 font-mono text-xs"
              placeholder={'Name | Detail | tag one, tag two | Action label | https://example.com | https://example.com/image.jpg'}
              value={listItemsDraft}
              onChange={(event) => {
                const nextValue = event.target.value;
                const nextItems = parseShareListItems(nextValue);
                setListItemsDraft(nextValue);
                onAssetChange?.({
                  ...asset,
                  listItems: nextItems,
                  size: `${nextItems.length} items`,
                });
              }}
            />
          </div>
        )}
        {editable && isLinkInBio && !isSingleLinkInBioUrl && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <label className="field-label mt-0" htmlFor={`asset-links-${asset.id}`}>Links</label>
            <textarea
              id={`asset-links-${asset.id}`}
              className="field min-h-36 font-mono text-xs"
              placeholder={'Portfolio | https://example.com\nBooking | https://example.com/book'}
              value={linkItemsToText(asset.linkItems || [])}
              onChange={(event) => updateAssetField('linkItems', parseLinkItems(event.target.value))}
            />
          </div>
        )}
        {editable && isMarkdownArticle && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <RichContentEditor
              id={`asset-markdown-${asset.id}`}
              label="Rich content"
              uploadImage={uploadRichContentImage}
              placeholder={'## Summary\n\nPaste Markdown, image links, YouTube links, or safe HTML embed code here.'}
              value={asset.articleMarkdown || ''}
              onChange={(nextValue) => updateAssetField('articleMarkdown', nextValue)}
            />
          </div>
        )}
        {editable && isForm && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <FormBuilder
              form={{ enabled: true, ...(asset.form || {}) }}
              linkItems={[]}
              onChange={(form) => onAssetChange?.({
                ...asset,
                form: { ...form, enabled: true },
                size: `${form.fields?.length || 0} fields`,
                title: form.title || asset.title,
                description: form.description || asset.description,
              })}
            />
          </div>
        )}
        {editable && (enabledFields.description || enabledFields.price || enabledFields.quantity || enabledFields.sizes || enabledFields.colors || showCheckoutCostFields) ? (
          <div className="mt-4 border-t border-slate-200 pt-4">
            {enabledFields.description && (
              <>
                <label className="field-label mt-0" htmlFor={`asset-description-${asset.id}`}>Description</label>
                <textarea
                  id={`asset-description-${asset.id}`}
                  className={`field resize-none ${isDescriptionExpanded ? 'min-h-36' : 'min-h-0'}`}
                  placeholder="Add asset details"
                  rows={isDescriptionExpanded ? 6 : 2}
                  value={asset.description || ''}
                  onChange={(event) => updateAssetField('description', event.target.value)}
                />
                <button
                  className="mt-2 text-sm font-semibold text-slate-600 transition hover:text-slate-950"
                  onClick={() => setIsDescriptionExpanded((current) => !current)}
                  type="button"
                >
                  {isDescriptionExpanded ? 'Show less' : 'Show more'}
                </button>
              </>
            )}
            {(enabledFields.price || enabledFields.quantity || enabledFields.sizes || enabledFields.colors || showCheckoutCostFields) && (
              <div className="grid gap-3">
                {enabledFields.price && (
                  <div>
                    <label className="field-label" htmlFor={`asset-price-${asset.id}`}>Price</label>
                    <div className="grid gap-2 sm:grid-cols-[1fr_1fr_2fr]">
                      <select
                        aria-label="Currency"
                        className="field min-w-0"
                        value={asset.currency || 'ZAR'}
                        onChange={(event) => updateAssetField('currency', event.target.value)}
                      >
                        {currencyOptions.map((currency) => (
                          <option key={currency} value={currency}>{currency}</option>
                        ))}
                      </select>
                      <input
                        aria-label="Price prefix"
                        className="field min-w-0"
                        placeholder="from"
                        value={asset.pricePrefix || ''}
                        onChange={(event) => updateAssetField('pricePrefix', event.target.value)}
                      />
                      <input
                        id={`asset-price-${asset.id}`}
                        className={`field min-w-0 ${isComboAsset ? 'bg-slate-100 text-slate-600' : ''}`}
                        inputMode="decimal"
                        placeholder="0.00"
                        readOnly={isComboAsset}
                        value={asset.price || ''}
                        onChange={(event) => {
                          if (!isComboAsset) updateStorePricingField('price', event.target.value);
                        }}
                      />
                    </div>
                    <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_2fr]">
                      <select
                        aria-label="Discount type"
                        className="field min-w-0"
                        value={asset.discountType || 'percentage'}
                        onChange={(event) => updateDiscountField('discountType', event.target.value)}
                      >
                        <option value="percentage">Reduce by percentage</option>
                        <option value="amount">Reduce by amount</option>
                      </select>
                      <input
                        aria-label="Discount value"
                        className="field min-w-0"
                        inputMode="decimal"
                        placeholder={asset.discountType === 'amount' ? 'Discount amount' : 'Discount percentage'}
                        value={asset.discountValue || ''}
                        onChange={(event) => updateDiscountField('discountValue', event.target.value)}
                      />
                    </div>
                    {hasReducedPrice && (
                      <p className="mt-2 text-xs font-semibold text-slate-500">
                        Reduced price: {priceLabel}
                      </p>
                    )}
                    <label className="field-label" htmlFor={`asset-prepay-${asset.id}`}>Prepay</label>
                    <input
                      id={`asset-prepay-${asset.id}`}
                      className="field"
                      placeholder="e.g. Prepay 50%"
                      value={asset.prepay || ''}
                      onChange={(event) => updateAssetField('prepay', event.target.value)}
                    />
                  </div>
                )}
                
                {enabledFields.quantity && (
                  <div>
                    <label className="field-label" htmlFor={`asset-quantity-${asset.id}`}>Quantity</label>
                    <input
                      id={`asset-quantity-${asset.id}`}
                      className="field"
                      min="0"
                      type="number"
                      value={asset.quantity || ''}
                      onChange={(event) => updateAssetField('quantity', event.target.value)}
                    />
                    {showStoreQuantityHint && (
                      <p className="mt-1 text-xs font-semibold text-red-600">
                        Hidden from checkout when Qty is 0 or empty.
                      </p>
                    )}
                  </div>
                )}
                {showCheckoutCostFields && (
                  <>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <label className="field-label" htmlFor={`asset-unit-cost-${asset.id}`}>Unit cost</label>
                        <input
                          id={`asset-unit-cost-${asset.id}`}
                          className={`field ${isComboAsset ? 'bg-slate-100 text-slate-600' : ''}`}
                          inputMode="decimal"
                          placeholder="0.00"
                          readOnly={isComboAsset}
                          value={asset.unitCost || ''}
                          onChange={(event) => {
                            if (!isComboAsset) updateStorePricingField('unitCost', event.target.value);
                          }}
                        />
                      </div>
                      <div>
                        <label className="field-label" htmlFor={`asset-profit-${asset.id}`}>Profit</label>
                        <input
                          id={`asset-profit-${asset.id}`}
                          className="field bg-slate-100 text-slate-600"
                          inputMode="decimal"
                          placeholder="0.00"
                          readOnly
                          value={asset.profit || ''}
                        />
                      </div>
                    </div>
                    <ComboProductEditor
                      assetId={asset.id}
                      productOptions={comboProductOptions}
                      value={asset.comboProductIds}
                      onChange={updateComboProducts}
                    />
                  </>
                )}
                {enabledFields.sizes && (
                  <OptionTagEditor
                    inputId={`asset-sizes-${asset.id}`}
                    label="Available sizes"
                    onChange={(nextOptions) => updateAssetField('availableSizes', nextOptions)}
                    placeholder="Small"
                    value={asset.availableSizes}
                  />
                )}
                {enabledFields.colors && (
                  <OptionTagEditor
                    inputId={`asset-colors-${asset.id}`}
                    label="Available colors"
                    onChange={(nextOptions) => updateAssetField('availableColors', nextOptions)}
                    placeholder="Black"
                    value={asset.availableColors}
                  />
                )}
              </div>
            )}
          </div>
        ) : hasDescription && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <div className={`asset-description text-sm text-slate-600 ${isDescriptionExpanded ? '' : 'line-clamp-2'}`}>
              <ReactMarkdown>{asset.description}</ReactMarkdown>
            </div>
            <button
              className="mt-2 text-sm font-semibold text-slate-600 transition hover:text-slate-950"
              onClick={() => setIsDescriptionExpanded((current) => !current)}
              type="button"
            >
              {isDescriptionExpanded ? 'Show less' : 'Show more'}
            </button>
          </div>
        )}
        {hasAssetActions && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            {asset.listingLogoUrl ? (
              <img
                alt={`${asset.title || 'Listing'} logo`}
                className="asset-listing-action-logo"
                src={asset.listingLogoUrl}
              />
            ) : <span aria-hidden="true" />}
            <div className="flex flex-wrap justify-end gap-2">
              {canViewDetail && !showMediaDetailOverlay && (
                <button
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                  onClick={() => setIsDetailOpen(true)}
                  type="button"
                >
                  Details
                </button>
              )}
              {canAddToCart && (
                <button
                  className="rounded bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                  disabled={addToCartDisabled}
                  onClick={() => onAddToCart?.(asset)}
                  type="button"
                >
                  {cartButtonLabel?.trim() || 'Add to cart'}
                </button>
              )}
              {canEnquire && (
                <button
                  className="rounded bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  onClick={() => onEnquire?.(asset)}
                  type="button"
                >
                  Enquire
                </button>
              )}
              {canAddToItemForm && (
                <button
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                  onClick={() => onAddToItemForm?.(asset)}
                  type="button"
                >
                  {itemFormButtonText?.trim() || 'Item form'}
                </button>
              )}
              {canAddUpdate && (
                <button
                  className="rounded bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  onClick={() => onAddUpdate?.(asset)}
                  type="button"
                >
                  Update
                </button>
              )}
              {canDownload && (
                <a
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                  download={asset.title}
                  href={assetUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  Download
                </a>
              )}
              {canShare && (
                <button
                  aria-label="Share asset"
                  className="grid h-9 w-9 place-items-center rounded border border-slate-300 bg-white text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                  onClick={handleShare}
                  title="Share asset"
                  type="button"
                >
                  <ShareIcon />
                </button>
              )}
            </div>
          </div>
        )}
        {shareStatus && <p className="mt-2 text-xs font-semibold text-slate-500">{shareStatus}</p>}
      </div>
      )}
    </article>
    {isDetailOpen && (
      <div
        className="modal-backdrop"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) setIsDetailOpen(false);
        }}
        role="presentation"
      >
        <aside
          aria-labelledby={`asset-detail-title-${asset.id}`}
          aria-modal="true"
          className="asset-detail-modal"
          role="dialog"
        >
          <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
            <div className="min-w-0">
              <p className="label-caps">{displayAssetType(asset)}</p>
              <h2 className="mt-1 text-xl font-semibold leading-snug text-slate-950" id={`asset-detail-title-${asset.id}`}>
                {asset.title || asset.name || 'Untitled asset'}
              </h2>
            </div>
            <button
              aria-label="Close item details"
              className="icon-button shrink-0"
              onClick={() => setIsDetailOpen(false)}
              type="button"
            >
              x
            </button>
          </div>

          <div className="grid gap-5 p-5">
            <div className="asset-detail-preview">
              {renderDetailPreview()}
            </div>

            <div className="grid gap-4">
              <div className="flex flex-wrap gap-2">
                {hasCategory && <span className="chip">{asset.category}</span>}
                {hasPrice && (
                  <span className="asset-price-badge">
                    {hasReducedPrice && <span className="asset-price-original">{originalPriceLabel}</span>}
                    {pricePrefix && <span className="asset-price-prefix">{pricePrefix}</span>}
                    <span className="asset-price-current">{priceLabel}</span>
                    {hasPrepay && <span className="asset-prepay-label">{asset.prepay}</span>}
                  </span>
                )}
                {hasQuantity && <span className="chip">Qty: {asset.quantity}</span>}
                {hasArea && <span className="chip">{asset.area}</span>}
                {asset.size && <span className="chip">{asset.size}</span>}
                {asset.status && editable && <span className="chip">{asset.status}</span>}
              </div>
              {hasListingSpecs && <ListingSpecs specs={listingSpecs} />}

              {(hasSizes || hasColors) && (
                <div className="flex flex-wrap gap-2">
                  {hasSizes && <span className="chip">Sizes: {availableSizes.join(', ')}</span>}
                  {hasColors && <span className="chip">Colors: {availableColors.join(', ')}</span>}
                </div>
              )}

              {asset.description && !isLinkInBio && !isShareList && (
          <div className="asset-description text-xs leading-5 text-slate-600">
            <ReactMarkdown>{asset.description}</ReactMarkdown>
          </div>
              )}

              {itemImages.length > 0 && (
                <div className="asset-item-image-gallery">
                  {itemImages.map((image) => {
                    const imageUrl = imageUrlForItemImage(image);
                    if (!imageUrl) return null;

                    return (
                      <a
                        href={imageUrl}
                        key={image.id || imageUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <img src={imageUrl} alt={image.name || `${asset.title || 'Item'} image`} />
                      </a>
                    );
                  })}
                </div>
              )}

              {canOpenAssetLink && (
                <a
                  className="rounded border border-slate-300 bg-white px-3 py-2 text-center text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                  href={assetLinkUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  Open profile
                </a>
              )}

              {hasAssetActions && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
                  {asset.listingLogoUrl ? (
                    <img
                      alt={`${asset.title || 'Listing'} logo`}
                      className="asset-listing-action-logo"
                      src={asset.listingLogoUrl}
                    />
                  ) : <span aria-hidden="true" />}
                  <div className="flex flex-wrap justify-end gap-2">
                    {canAddToCart && (
                      <button
                        className="rounded bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                        disabled={addToCartDisabled}
                        onClick={() => onAddToCart?.(asset)}
                        type="button"
                      >
                        {cartButtonLabel?.trim() || 'Add to cart'}
                      </button>
                    )}
                    {canEnquire && (
                      <button
                        className="rounded bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                        onClick={() => onEnquire?.(asset)}
                        type="button"
                      >
                        Enquire
                      </button>
                    )}
                    {canAddToItemForm && (
                      <button
                        className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                        onClick={() => onAddToItemForm?.(asset)}
                        type="button"
                      >
                        {itemFormButtonText?.trim() || 'Item form'}
                      </button>
                    )}
                    {canAddUpdate && (
                      <button
                        className="rounded bg-slate-950 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                        onClick={() => onAddUpdate?.(asset)}
                        type="button"
                      >
                        Update
                      </button>
                    )}
                    {canDownload && (
                      <a
                        className="rounded border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                        download={asset.title}
                        href={assetUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        Download
                      </a>
                    )}
                    {canShare && (
                      <button
                        aria-label="Share asset"
                        className="grid h-9 w-9 place-items-center rounded border border-slate-300 bg-white text-slate-700 transition hover:border-slate-950 hover:text-slate-950"
                        onClick={handleShare}
                        title="Share asset"
                        type="button"
                      >
                        <ShareIcon />
                      </button>
                    )}
                  </div>
                </div>
              )}
              {shareStatus && <p className="text-xs font-semibold text-slate-500">{shareStatus}</p>}
            </div>
          </div>
        </aside>
      </div>
    )}
    </>
  );
}

export default AssetCard;
